import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../api/client';
import { Spinner } from '../components/ui/Spinner';
import { AuthShell } from './AuthShell';
import { SsoButtons } from './SsoButtons';
import './LoginPage.css';

export function LoginPage() {
  const { login, isAuthenticated, isLoading, isEkomAdmin } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // An SSO failure routes back here via /auth/callback with the copy in router
  // state (see AuthCallbackPage) — seed the error slot from it on first render.
  const [error, setError] = useState(
    () => (location.state as { error?: string } | null)?.error ?? '',
  );
  const [submitting, setSubmitting] = useState(false);

  if (isLoading) return <Spinner fullscreen />;
  if (isAuthenticated) {
    return <Navigate to={isEkomAdmin ? '/admin' : '/'} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      // AuthProvider handles navigation post-login based on membership role
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <form className="login-form-inner" onSubmit={handleSubmit} noValidate>
        <h1 className="login-form-heading">Sign in</h1>

        {error && <div className="login-error">{error}</div>}

        <div className="login-field-wrap">
          <label className="login-field-label" htmlFor="login-email">
            Email
          </label>
          <input
            id="login-email"
            className="login-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="your@company.com"
            autoComplete="email"
            required
            disabled={submitting}
          />
        </div>

        <div className="login-field-wrap">
          <label className="login-field-label" htmlFor="login-password">
            Password
          </label>
          <input
            id="login-password"
            className="login-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            placeholder="••••••••••"
            autoComplete="current-password"
            required
            disabled={submitting}
          />
        </div>

        <button type="submit" className="login-btn-signin" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="login-divider">
          <span>or</span>
        </div>

        <SsoButtons setError={setError} />
      </form>
    </AuthShell>
  );
}
